import React, { useMemo } from 'react';
import clsx from 'clsx';
import type { PublicAssetShare } from 'fym-shared/src/music24/assetShares';
import { TypedMemo } from '../../common/types';
import { useSMLTheme } from '../theme/useSMLTheme';
import { publicShareKindOf } from './assetShareFacts';
import { shareArtColours, shareArtStyle } from './assetShareColours';
import { AssetShareAmbient } from './AssetShareAmbient';
import { AssetShareMedia } from './AssetShareMedia';
import { AssetShareIdentity } from './AssetShareIdentity';
import { AssetShareActions } from './AssetShareActions';
import type { OpenInRelease } from './AssetShareActions';
import { AssetShareFoot, AssetShareHook } from './AssetShareGrowth';
import { AssetShareToast } from './AssetShareToast';
import { useShareLink } from './useShareLink';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * design: asset-share c1-c5 (Theater). The file is the page: the player is as large as the window
 * allows, a blurred copy of the file lights the page around it, and one info strip holds the
 * song, the artist, the kind and the actions. Light and dark follow the theme of the app. On a
 * phone the player goes edge to edge, and the growth line "Made with Music24" sits at the end of a
 * video page.
 */
const AssetShareTheaterComponent: React.FC<{ share: PublicAssetShare; openInRelease: OpenInRelease | null }> = ({
  share,
  openInRelease,
}) => {
  const { theme } = useSMLTheme();
  const artStyle = useMemo(() => shareArtStyle(shareArtColours(share.look, theme)), [share.look, theme]);
  const { copied, copyLink, shareLink } = useShareLink(share.song.title, publicShareKindOf(share.assetLabel));
  const isVideo = share.mediaKind === 'video';

  return (
    <div
      className={clsx(styles.page, styles.theater, theme === 'light' ? styles.light : styles.dark)}
      style={artStyle}
      data-testid="asset-share-public"
      data-layout="theater"
    >
      <AssetShareAmbient share={share} />
      <div className={clsx(styles.theaterInner, !isVideo && styles.theaterPicture)}>
        <AssetShareMedia share={share} />
        <div className={styles.strip}>
          <AssetShareIdentity share={share} onShare={shareLink} />
          <AssetShareActions share={share} onCopyLink={copyLink} openInRelease={openInRelease} />
        </div>
        <div className={styles.growth}>
          {isVideo && <AssetShareHook kind={publicShareKindOf(share.assetLabel)} className="d-lg-none" />}
          <AssetShareFoot
            kind={publicShareKindOf(share.assetLabel)}
            className={isVideo ? 'd-none d-lg-flex' : undefined}
          />
        </div>
      </div>
      {copied && <AssetShareToast />}
    </div>
  );
};

export const AssetShareTheater = TypedMemo(AssetShareTheaterComponent);
